import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { inject, observer } from 'mobx-react';
import { action, observable, reaction, computed } from 'mobx';
import { parse as qs_parse, stringify as qs_stringify } from 'query-string';
import { render_date, render_amount, render_po_number } from 'utils';
import { authorize } from 'utils/auth';
import { getDateFormat } from 'ui/i18n/formats';
import { QS_DATE_FORMAT, MONTHS_IN_YEAR } from 'config/constants';
import { FormattedMessage } from 'ui/i18n';

import PaginationOptions from 'utils/domain/PaginationOptions';
import SortingOptions from 'utils/domain/SortingOptions';

import {
	InfoBox,
	DataTable,
	Alert,
	ConcernMessage,
	Loader,
	OrderNav,
	DatePicker,
	WebReportsLink,
} from 'ui/components';

import './OrderStatus.scss';

function getColumns(country) {
	return [
		{
			prop: 'date',
			propToSort: 'po_number_sort',
			title: <FormattedMessage id="ordersTable.orderDateColumn" />,
			align: 'center',
			render(row) {
				return render_date(row[this.prop]);
			},
			sortable: true,
			noWrap: true,
		},
		{
			prop: 'po_number',
			title: <FormattedMessage id="ordersTable.orderReference" />,
			align: 'left',
			render(row) {
				return render_po_number(row[this.prop]);
			},
			sortable: true,
		},
		{
			prop: 'type',
			title: <FormattedMessage id="ordersTable.orderTypeColumn" />,
			align: 'left',
			sortable: true,
		},
		{
			prop: 'ship_to_name',
			title: <FormattedMessage id="ordersTable.shipToNameColumn" />,
			sortable: true,
			align: 'left',
		},
		{
			prop: 'total',
			title: (
				<FormattedMessage
					id="ordersTable.totalColumn"
					values={{ currency: country }}
				/>
			),
			align: 'right',
			render(row) {
				return render_amount(row[this.prop]);
			},
			sortable: true,
			noWrap: true,
		},
	];
}

@authorize({ rights: ['GetOrderHistory'] })
@inject('OrdersState', 'AppState')
@observer
export default class OrderStatusPage extends Component {
	static propTypes = {
		AppState: PropTypes.object.isRequired,
		OrdersState: PropTypes.object.isRequired,
		history: PropTypes.object.isRequired,
		location: PropTypes.object.isRequired,
	};

	constructor(props) {
		super(props);
		this.shouldClearCache();
		this.addPageToHistory();
	}

	componentWillMount() {
		/** Reload data */
		reaction(
			() => [this.pagination, this.order],
			() =>
				this.OrdersState.fetchOrderStatus({
					start_date: moment.utc(
						this.current_start_date,
						this.getDateTimeFormat()
					),
					end_date: moment(this.current_end_date, this.getDateTimeFormat()),
					sorting: this.order,
					pagination: this.pagination,
				}),
			true
		);

		/** Sync query string */
		reaction(
			() => [this.current_start_date, this.current_end_date],
			() =>
				this.props.history.replace({
					search: qs_stringify({
						start: this.toMoment(this.current_start_date).format(
							QS_DATE_FORMAT
						),
						end: this.toMoment(this.current_end_date).format(QS_DATE_FORMAT),
					}),
				}),
			false
		);

		reaction(
			() => this.OrdersState.fetchOrderStatus.state.result,
			result => this.setTotalCount(result ? result.count : 0)
		);
	}

	AppState = this.props.AppState;
	OrdersState = this.props.OrdersState;
	disabledDays = [
		{
			after: new Date(),
			before: moment().subtract(MONTHS_IN_YEAR, 'months').toDate(),
		},
	];

	@observable order = new SortingOptions('po_number_sort', 'desc');
	@observable pagination = new PaginationOptions();

	@observable start_date = this.getDefaultDate('start_date');
	@observable end_date = this.getDefaultDate('end_date');

	@observable date_interval_error = false;
	@observable current_start_date = this.getDefaultDate('start_date');
	@observable current_end_date = this.getDefaultDate('end_date');
	@observable totalCount = null;
	MIN_HISTORY_LENGTH = 1;
	locations = this.props.AppState.locations;

	shouldClearCache() {
		const shouldClearCache =
			this.props.location.state && this.props.location.state.fromSubmitOrder
				? this.props.location.state.fromSubmitOrder
				: false;

		if (shouldClearCache) {
			this.props.history.replace({
				pathname: this.props.location.pathname,
				state: {},
			});
			window.location.reload();
		}
	}

	addPageToHistory() {
		if (this.locations.length < this.MIN_HISTORY_LENGTH) {
			this.locations.push(window.location.pathname);
		}
	}

	@computed
	get canShowResults() {
		return this.date_interval_error;
	}

	getDatesFromQuery() {
		const query = qs_parse(window.location.search);
		const dates = {};
		const format = this.getDateFormat();

		if (query.start && query.end) {
			dates.start_date = moment.utc(query.start, QS_DATE_FORMAT).format(format);
			dates.end_date = moment.utc(query.end, QS_DATE_FORMAT).format(format);

			return dates;
		}

		return null;
	}

	/* By default we show the last 30 days but if we have dates in the URL use those */
	getDefaultDate(which) {
		let dates = this.getDatesFromQuery();
		if (dates) {
			return dates[which];
		}

		const format = this.getDateFormat();

		dates = {
			start_date: this.toMoment(new Date())
				.subtract(1, 'months')
				.format(format),
			end_date: this.toMoment(new Date()).format(format),
		};

		return dates[which];
	}

	getDateFormat() {
		return getDateFormat(
			this.AppState.user.country.short_name,
			this.props.AppState.locale
		).standard;
	}

	getDateTimeFormat() {
		return getDateFormat(
			this.AppState.user.country.short_name,
			this.props.AppState.locale
		).datetime;
	}

	toMoment(date) {
		return moment(date, this.getDateFormat());
	}

	@action
	handleDateChange(which, date) {
		this[which] = date;

		this.validateDates();
		this.validateDateInterval();
	}

	@action
	validateDates() {
		this.date_interval_error = false;

		['start_date', 'end_date'].forEach(which => {
			if (!moment(this[which], this.getDateFormat(), true).isValid()) {
				this.date_interval_error = `dates.invalid_${which}`;
			}
		});
	}

	@action
	validateDateInterval() {
		if (this.date_interval_error) {
			return;
		}

		const start_date = this.toMoment(this.start_date);
		const end_date = this.toMoment(this.end_date);

		/* make sure we don't search in the future */
		const today = moment();
		if (end_date.isAfter(today)) {
			this.date_interval_error = 'dates.cant_see_future';
		}

		if (moment().subtract(1, 'year').isAfter(start_date, 'day')) {
			this.date_interval_error = 'dates.too_far_back';
		}

		/* how can start be after end? */
		if (start_date.isAfter(end_date)) {
			this.date_interval_error = 'dates.start_date_after_end';
		}
	}

	@action
	handlePaginationChange(pagination) {
		this.pagination = pagination;
		this.handleDateChange('start_date', this.current_start_date);
		this.handleDateChange('end_date', this.current_end_date);
	}

	@action
	handleOrderChange(order) {
		this.order = order;
	}

	@action
	handleShowResults() {
		this.shouldClearCache = false;
		this.current_start_date = this.start_date;
		this.current_end_date = this.end_date;
		this.pagination = new PaginationOptions();
	}

	@action
	setTotalCount(value) {
		this.totalCount = value;
	}

	render() {
		const { fetchOrderStatus } = this.OrdersState;
		const { loading } = fetchOrderStatus.state;
		const userCountry = this.props.AppState.user.country.short_name;
		const { locale } = this.AppState;

		return (
			<div>
				<OrderNav />
				<div className="OrderStatus">
					<WebReportsLink />
					<div className="row OrderTitle">
						<div id="OrderStatus-count" className="col-md-6 leftSide">
							<FormattedMessage id="navigation.orderHistory" />:{' '}
							{this.totalCount} <FormattedMessage id="orderHistory.itemsIn" />
						</div>
					</div>
					<hr />
					{this.date_interval_error &&
						<Alert type="danger" msgId={this.date_interval_error} />}
					<div className="row FiltersBar FilterControls">
						<div className="form-group">
							<label>
								<FormattedMessage id="orderHistory.startDate" />
							</label>
							<div className="datepicker__input-container">
								<DatePicker
									id="OrderStatus-StartDate-input"
									selected={this.start_date}
									format={this.getDateFormat()}
									onChange={this.handleDateChange.bind(this, 'start_date')}
									disabled={loading}
									disabledDays={this.disabledDays}
									locale={locale}
								/>
							</div>
						</div>

						<div className="form-group">
							<label>
								<FormattedMessage id="orderHistory.endDate" />
							</label>
							<div className="datepicker__input-container">
								<DatePicker
									id="OrderStatus-EndDate-input"
									selected={this.end_date}
									format={this.getDateFormat()}
									onChange={this.handleDateChange.bind(this, 'end_date')}
									disabled={loading}
									disabledDays={this.disabledDays}
									locale={locale}
								/>
							</div>
						</div>

						<div className="form-group">
							<button
								id="OrderStatus-show-results"
								className="btn btn-primary"
								onClick={this.handleShowResults.bind(this)}
								disabled={this.canShowResults}
							>
								<FormattedMessage id="orderHistory.showResults" />
							</button>
						</div>
					</div>
					{fetchOrderStatus.handle({
						loading: () => <Loader />,
						rejected: error =>
							<InfoBox type="danger">
								<FormattedMessage id={error.message} />
							</InfoBox>,
						resolved: results =>
							results.data && results.data.length
								? <DataTable
										id="OrderStatus-results-table"
										columns={getColumns(
											this.AppState.user.country.currency.symbol || ''
										)}
										rows={results.data}
										order={this.order}
										onOrderChange={this.handleOrderChange.bind(this)}
										count={results.count}
										pagination={this.pagination}
										onPaginationChange={this.handlePaginationChange.bind(this)}
									/>
								: <InfoBox type="info">
										<FormattedMessage id="orderHistory.noOrdersFound" />
									</InfoBox>,
					})}
					<ConcernMessage userCountry={userCountry} />
				</div>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/ui/pages/OrderStatus/index.js